import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Stack, Box, Tooltip,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  listAccessAnalyzerMembers, addAccessAnalyzerMembers, removeAccessAnalyzerMember,
  retryAccessAnalyzerMember, getAccessAnalyzerConfig,
} from '../../../services/accessAnalyzerApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalSectionHeader, PortalErrorState,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

const ACCESS_ANALYZER_COLOR = '#AD1457';
const ACCESS_ANALYZER_COLOR_DARK = '#8C1049';

const accessAnalyzerButtonSx = {
  backgroundColor: ACCESS_ANALYZER_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: ACCESS_ANALYZER_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which accounts IAM Access Analyzer analyzes"
    color={ACCESS_ANALYZER_COLOR}
  />
);

// The complete per-cell phase vocabulary of AccessAnalyzerMemberStatus, which is a
// STRICT SUBSET of the shared phaseWeight map - no phase of this capability's own.
//
// "disabled" is labelled "Removed", never "Disabled". It is a tombstone: the cell
// is only written after the account's analyzers were torn down AND its IAM grant
// detached, so it describes an account that is gone, not one that is present but
// switched off. Rendering it with the raw word has repeatedly been read as the
// latter.
const PHASE_META = {
  pending: { intent: 'neutral', label: 'Pending' },
  granting: { intent: 'info', label: 'Granting access' },
  enabling: { intent: 'info', label: 'Creating analyzer' },
  enabled: { intent: 'success', label: 'Enabled' },
  failed: { intent: 'error', label: 'Failed' },
  removing: { intent: 'warning', label: 'Removing' },
  disabled: { intent: 'neutral', label: 'Removed' },
};

const phaseMeta = (p) => PHASE_META[p] || { intent: 'neutral', label: formatStatusLabel(p) };

// Phases during which the backend is still working on a cell. "granting" belongs
// here: the grant gate assumes a role and waits for IAM propagation in every
// enrolled account, which runs for minutes, and a page that stops polling through
// it renders a frozen bar over a perfectly healthy operation.
const ACTIVE_PHASES = ['pending', 'granting', 'enabling', 'removing'];

// AWS's own analyzer type, which is what makes this grid unlike every other
// capability's: it is NOT a plain (account × region) product. External access needs
// one analyzer per region because Access Analyzer only evaluates policies on
// resources in the analyzer's own region; unused access is region-INDEPENDENT (IAM
// roles and users are global) and is billed per analyzer, so Prism creates exactly
// one per account, at the home region. Fanning it across regions would bill the
// customer once per region for byte-identical findings.
const ANALYZER_TYPE_META = {
  ACCOUNT: { label: 'External access', intent: 'info' },
  ACCOUNT_UNUSED_ACCESS: { label: 'Unused access', intent: 'purple' },
};
const analyzerTypeMeta = (t) => ANALYZER_TYPE_META[t] || { label: t || 'Analyzer', intent: 'neutral' };

// AWS's AnalyzerStatus, which is NOT Prism's phase: a cell can be phase=enabled
// while AWS reports FAILED, and that disagreement is exactly what an operator needs
// to see. Prism's phase says "we finished our work"; this says "AWS thinks the
// analyzer is healthy".
//
// All four AWS values get their own intent, because the three unhealthy ones mean
// three different things. ACTIVE is the steady state and is the only one drawn as
// success. DISABLED is amber rather than grey: a disabled analyzer produces no
// findings at all, which is a condition to act on and not the inert, neutral fact
// that grey reads as next to a green "Enabled" phase chip.
const ANALYZER_STATUS_META = {
  ACTIVE: {
    intent: 'success',
    label: 'Active',
    hint: 'AWS reports this analyzer is active and analyzing.',
  },
  CREATING: {
    intent: 'info',
    label: 'Creating',
    hint: 'AWS was still creating this analyzer. It becomes active on its own, with no action here.',
  },
  DISABLED: {
    intent: 'warning',
    label: 'Disabled',
    hint: 'AWS reports this analyzer is disabled, so it is producing no findings.',
  },
  FAILED: {
    intent: 'error',
    label: 'Failed',
    hint: 'AWS reports this analyzer failed. It will produce no findings until it is re-created - use Retry on the account.',
  },
};

// The freshness caveat, stated in every AWS-status tooltip and once in the table
// footnote. The value is re-read from AWS during a findings refresh, so a cell
// enrolled since the last refresh legitimately still reads "Creating" long after
// AWS has made the analyzer active. Without saying so, the chip looks like a live
// health poll and an operator waits on a number that only moves on refresh.
const AWS_STATUS_FRESHNESS = 'This is as of the last findings refresh, not a live poll.';

const analyzerStatusMeta = (s) => ANALYZER_STATUS_META[s] || {
  intent: 'neutral',
  label: formatStatusLabel(s),
  hint: 'Status reported by AWS for this analyzer.',
};

// What the status means, then AWS's own reason when it gave one, then the caveat.
const analyzerStatusTitle = (meta, reason) => [
  meta.hint,
  reason ? `AWS reason: ${reason}` : '',
  AWS_STATUS_FRESHNESS,
].filter(Boolean).join(' ');

// Cells sort external-access first, then unused access, and alphabetically by
// region inside each family - so the home region's two cells sit next to each
// other and the grid reads the same way for every account.
const cellOrder = (a, b) => {
  if (a.analyzerType !== b.analyzerType) return a.analyzerType === 'ACCOUNT' ? -1 : 1;
  return String(a.region).localeCompare(String(b.region));
};

// The one chip that summarises an account whose cells disagree - which, in the flat
// peer model, is the normal case rather than an error state. "failed" wins because
// it is the only phase that carries an action.
const summaryPhase = (phases) => {
  if (phases.includes('failed')) return 'failed';
  for (const p of ['removing', 'granting', 'enabling', 'pending']) {
    if (phases.includes(p)) return p;
  }
  if (phases.every((p) => p === 'disabled')) return 'disabled';
  return 'enabled';
};

// AccessAnalyzerConfigResponse.linkingRegions is a decoded []string, but tolerate a
// raw JSON string so the page never blanks if the DTO shape shifts.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// Membership and region/unused-access mutations are refused by the backend unless
// the config is settled, so every button that dispatches one has to be gated on the
// same condition or it produces a guaranteed 400.
const configAllowsMemberOps = (status) => status === 'enabled' || status === 'partial_failure';

// One label/value row of the configuration card.
function ConfigRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 150, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function AccessAnalyzerEnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // showSpinner is false for post-mutation refreshes: swapping the whole page for a
  // loading state after every Add/Remove/Retry throws away the table the user is
  // watching, and the async work has only just been dispatched anyway.
  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      // Config + members decide whether Access Analyzer is set up at all; these
      // must succeed for the page to render an enrollment state.
      const [c, m] = await Promise.all([getAccessAnalyzerConfig(), listAccessAnalyzerMembers()]);
      const rows = m?.data || [];
      setConfig(c?.data || null);
      setMembers(rows);
      // A poll can enroll an account that is still ticked in the picker below -
      // most obviously the ones this page just submitted. Re-sending an enrolled
      // account is skipped server-side, and if EVERY tick is already enrolled the
      // whole request is refused with "no new accounts to add", which reads as a
      // failure of an Add that in fact succeeded.
      const live = new Set(rows.filter((r) => r.phase !== 'disabled').map((r) => r.accountId));
      setSelected((prev) => ([...prev].some((id) => live.has(id))
        ? new Set([...prev].filter((id) => !live.has(id)))
        : prev));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
    // Candidate-account list is best-effort: never let it blank the page. No
    // `|| a?.data` fallback: the envelope's data IS an object, so falling back to
    // it would hand an object to the .filter() below instead of reaching `[]`.
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || []);
    } catch (_) {
      setAccounts([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while EITHER a cell is mid-flight OR the config is mid-operation. The
  // config half is not redundant: an update claims the config before it touches a
  // single cell, so keying only on cell phases leaves the bar frozen for the whole
  // opening stretch of every operation.
  //
  // IMPORTANT: do NOT put clearInterval in this effect's cleanup. The interval
  // callback calls setMembers(next) on every tick, which produces a new array
  // reference, so [members] changes and React fires the cleanup - killing the
  // interval and restarting its countdown on every tick. That destroy-and-recreate
  // cycle is what freezes config.progress in the rendered bar. Instead the interval
  // stops itself when nothing is active (the inner check below), and the separate
  // unmount effect handles cleanup.
  useEffect(() => {
    const active = config?.status === 'in_progress'
      || members.some((m) => ACTIVE_PHASES.includes(m.phase));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const [m, c] = await Promise.all([listAccessAnalyzerMembers(), getAccessAnalyzerConfig()]);
          const next = m?.data || [];
          const nextCfg = c?.data || null;
          setMembers(next);
          setConfig(nextCfg);
          const stillActive = nextCfg?.status === 'in_progress'
            || next.some((r) => ACTIVE_PHASES.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) { /* transient */ }
      }, POLL_INTERVAL_MS);
    }
  }, [members, config?.status]);

  // Stop the interval when the component unmounts.
  useEffect(() => () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const operationRunning = config?.status === 'in_progress'
    || members.some((m) => ACTIVE_PHASES.includes(m.phase));

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrollment…" color={ACCESS_ANALYZER_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. A rejection from GET /config or GET
  // /members leaves `config` null, so without this a 500 renders as "Access
  // Analyzer is not set up yet" and points the operator at the setup wizard for a
  // capability that is configured and running - while the real message, already
  // captured in `error`, is discarded with the rest of the normal page.
  if (error && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/accessanalyzer')}>
              Go to setup
            </Button>
          }>
            IAM Access Analyzer is not set up yet for this customer. Complete the
            setup wizard first to enroll accounts.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  // One cell per (account, region, analyzer type) collapses to one row per account,
  // because every action the API offers - retry, remove - is account-scoped: a
  // single retry re-drives every failed cell the account owns, across both analyzer
  // families. The per-cell truth is not thrown away: it is rendered as a line per
  // cell inside the row, which is the only place the two families, the adopted flag
  // and AWS's own analyzer status are visible.
  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, cells: [], phases: [], errors: [], retries: 0, adopted: 0,
    };
    prev.cells.push(m);
    prev.phases.push(m.phase);
    if (m.errorMessage) prev.errors.push(`${m.region} ${analyzerTypeMeta(m.analyzerType).label}: ${m.errorMessage}`);
    prev.retries = Math.max(prev.retries, m.retryCount || 0);
    if (m.adopted) prev.adopted += 1;
    byAccount.set(m.accountId, prev);
  }
  byAccount.forEach((row) => row.cells.sort(cellOrder));

  // An account whose every cell is a tombstone is NOT enrolled. It keeps its rows
  // forever (they are the grant-detach ledger the backend needs to clean up a
  // leaked policy), so filtering has to happen here: leaving it in the enrolled
  // table would show a removed, un-granted account as a live one. It reappears
  // under "Not enrolled" instead, flagged as previously removed, which is also the
  // path that re-enrolls it.
  const allRows = [...byAccount.values()];
  const enrolledAccounts = allRows.filter((row) => summaryPhase(row.phases) !== 'disabled');
  const removedIds = new Set(
    allRows.filter((row) => summaryPhase(row.phases) === 'disabled').map((r) => r.accountId),
  );
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));
  const adoptedCells = enrolledAccounts.reduce((n, r) => n + r.adopted, 0);

  // GET /securityhub/accounts returns {accounts:[{accountId, name, status}]} -
  // `name` is the only name field there is. One label helper serves both tables so
  // an account cannot read differently depending on which one it is sitting in.
  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const canMutate = configAllowsMemberOps(config.status);
  // "failed" is terminal: no fan-out is running, and every mutation gate refuses it
  // until setup is re-run. It must never share the "waiting for the current
  // operation" copy, which promises a completion that will never arrive.
  const setupFailed = config.status === 'failed';
  const failedAccounts = enrolledAccounts.filter((r) => summaryPhase(r.phases) === 'failed');

  const run = async (fn) => {
    setBusy(true);
    let failure = null;
    try {
      await fn();
    } catch (e) {
      failure = e.message;
    }
    // Mutations answer {"status":"in_progress"} and dispatch the real work
    // asynchronously, so the response says nothing about the outcome. Every path
    // re-reads the members list, which is the only source of a row's true state.
    await load(false).catch(() => {});
    // AFTER the reload, never before: a successful load() clears `error`, so a
    // failure set first would be wiped a few milliseconds later and the rejected
    // Add/Remove/Retry would look like it went through.
    setError(failure);
    setBusy(false);
  };

  const onAdd = () => run(async () => {
    await addAccessAnalyzerMembers([...selected]);
    setSelected(new Set());
  });

  // Close the dialog before dispatching, not after it succeeds: a rejection is
  // reported in the page-level banner, which an open modal would sit on top of.
  const onRemove = (accountId) => {
    setConfirmRemove(null);
    return run(() => removeAccessAnalyzerMember(accountId));
  };

  // Deliberately one account at a time, with no "retry all". RetryMember flips the
  // config to in_progress before it returns, and the next retry is then refused -
  // so a loop over the failed accounts would retry the first and report a spurious
  // error for the rest.
  const onRetry = (accountId) => run(() => retryAccessAnalyzerMember(accountId));

  const linkingRegions = toStringArray(config.linkingRegions);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Configuration"
          subtitle="IAM Access Analyzer runs standalone in each account - there is no delegated administrator"
          color={ACCESS_ANALYZER_COLOR}
        />
        <ConfigRow label="Home region" value={config.homeRegion} />
        <ConfigRow label="Analyzed regions" value={linkingRegions.join(', ')} />
        <ConfigRow
          label="Unused access"
          value={config.unusedAccessEnabled
            ? `On - ${config.unusedAccessAge}-day window, ${config.homeRegion} only (billed)`
            : 'Off'}
        />
        <ConfigRow label="Adopted analyzers" value={String(config.adoptedCount ?? adoptedCells)} />
        <ConfigRow label="Status" value={formatStatusLabel(config.status)} />
      </Paper>
      </motion.div>

      {/* The page still renders in full rather than early-returning like the
          disabled branch: the per-account table below is the only record of how far
          each account got, and it is the diagnostic an operator needs before
          re-running setup. Only the copy has to be honest that nothing is running. */}
      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/accessanalyzer')}>
                Re-run setup
              </Button>
            )}
          >
            IAM Access Analyzer setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own - enrollment
            changes stay unavailable until setup is re-run. The accounts below show
            how far each one got.
          </Alert>
        </motion.div>
      )}

      {/* partial_failure is not a failure banner. Some accounts enrolled and some
          did not, which is the expected shape of a per-account enablement, and the
          only thing the operator needs is the list of the ones to retry. */}
      {config.status === 'partial_failure' && failedAccounts.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {failedAccounts.length === 1
              ? '1 account did not finish enrolling.'
              : `${failedAccounts.length} accounts did not finish enrolling.`}
            {' '}
            Every other account is analyzing normally. Use <b>Retry</b> on the rows
            below - one retry re-drives every failed analyzer the account owns.
          </Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'Working…'}
            sx={{ mb: 3 }}
          />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Enrolled accounts"
          subtitle="One external-access analyzer per region, plus one unused-access analyzer per account when that family is on"
          color={ACCESS_ANALYZER_COLOR}
          action={(
            <Chip
              size="small"
              label={`${enrolledAccounts.length} enrolled`}
              sx={statusChipSx('info')}
            />
          )}
        />
        {/* The legend for the one marker an operator cannot infer. An adopted
            analyzer enables exactly like any other cell, so without a visible flag
            the asymmetry - teardown deliberately skips it - is invisible until
            someone disables the capability and finds analyzers still running. */}
        {adoptedCells > 0 && (
          <Alert severity="info" icon={<HandshakeOutlinedIcon />} sx={{ mb: 2 }}>
            {adoptedCells} {adoptedCells === 1 ? 'analyzer was' : 'analyzers were'} already
            present when Prism enrolled the account and {adoptedCells === 1 ? 'was' : 'were'}{' '}
            <b>adopted</b> rather than created. Prism reads their findings but will
            never delete them - on teardown or account removal they keep running,
            because deleting an analyzer destroys every finding it holds and those
            are yours. Adopted cells are marked in the table below.
          </Alert>
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Analyzers</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell align="right" sx={headCellSx}>Retries</TableCell>
                <TableCell align="right" sx={headCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrolledAccounts.map((row, index) => {
                const phase = summaryPhase(row.phases);
                const meta = phaseMeta(phase);
                return (
                  <TableRow key={row.accountId} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>{acctLabel(row.accountId)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack spacing={0.75}>
                        {row.cells.map((c) => {
                          const typeMeta = analyzerTypeMeta(c.analyzerType);
                          const cellPhase = phaseMeta(c.phase);
                          const awsMeta = analyzerStatusMeta(c.analyzerStatus);
                          return (
                            <Box
                              key={`${c.analyzerType}|${c.region}`}
                              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}
                            >
                              <Chip size="small" label={typeMeta.label} sx={statusChipSx(typeMeta.intent)} />
                              <Typography
                                sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.body }}
                              >
                                {c.region}
                              </Typography>
                              <Chip size="small" label={cellPhase.label} sx={statusChipSx(cellPhase.intent)} />
                              {/* AWS's own AnalyzerStatus, shown beside Prism's
                                  phase rather than folded into it: the two are
                                  different axes and their disagreement (phase
                                  enabled, AWS FAILED) is the whole diagnostic.
                                  Title Case, not the raw enum: the chip sits in a
                                  row of sentence-case chips and SHOUTING reads as
                                  an alarm even when the value is ACTIVE. */}
                              {c.analyzerStatus && (
                                <Tooltip title={analyzerStatusTitle(awsMeta, c.statusReason)}>
                                  <Chip
                                    size="small"
                                    label={`AWS: ${awsMeta.label}`}
                                    sx={statusChipSx(awsMeta.intent)}
                                  />
                                </Tooltip>
                              )}
                              {c.adopted && (
                                <Tooltip title="This analyzer already existed. Prism reads its findings and never deletes it - deleting an analyzer destroys every finding it holds.">
                                  <Chip
                                    size="small"
                                    icon={<HandshakeOutlinedIcon />}
                                    label="Adopted"
                                    sx={statusChipSx('success')}
                                  />
                                </Tooltip>
                              )}
                              {c.retryCount > 0 && (
                                <Chip
                                  size="small"
                                  label={`${c.retryCount} ${c.retryCount === 1 ? 'retry' : 'retries'}`}
                                  sx={statusChipSx('neutral')}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={meta.label} size="small" sx={statusChipSx(meta.intent)} />
                        {ACTIVE_PHASES.includes(phase) && (
                          <CircularProgress size={14} thickness={5} sx={{ color: ACCESS_ANALYZER_COLOR }} />
                        )}
                      </Stack>
                      {row.errors.length > 0 && (
                        <Typography
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            fontFamily: FONT_BODY,
                            fontSize: '0.75rem',
                            color: CK.red,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.errors.join(' • ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {row.retries > 0 ? row.retries : '-'}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {phase === 'failed' && (
                        <Button
                          size="small"
                          color="warning"
                          disabled={busy || !canMutate}
                          onClick={() => onRetry(row.accountId)}
                          sx={{ mr: 1, fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                        >
                          Retry
                        </Button>
                      )}
                      {phase !== 'removing' && (
                        <Button
                          size="small"
                          color="error"
                          disabled={busy || !canMutate}
                          onClick={() => setConfirmRemove(row.accountId)}
                          sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {enrolledAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                      title="No accounts enrolled yet"
                      description="Pick accounts from the list below and add them to start analyzing access."
                      color={ACCESS_ANALYZER_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* The tooltips carry this too, but the freshness of the AWS chip has to be
            legible without hovering: a just-enrolled cell reading "Creating" is the
            first thing an operator sees, and read as a live poll it looks stuck. */}
        {enrolledAccounts.length > 0 && (
          <Typography
            sx={{
              mt: 1.5,
              fontFamily: FONT_BODY,
              fontSize: '0.75rem',
              color: CK.muted,
            }}
          >
            <b>AWS:</b> chips show the status AWS last reported for that analyzer.
            Prism re-reads it when findings are refreshed rather than polling
            continuously, so an analyzer enrolled since the last refresh can still
            read <b>Creating</b> here after AWS has made it active.
          </Typography>
        )}
      </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<GroupAddIcon sx={{ fontSize: 20 }} />}
          title="Not enrolled"
          subtitle="Linked accounts that IAM Access Analyzer is not analyzing"
          color={ACCESS_ANALYZER_COLOR}
          action={(
            <Button
              variant="contained"
              disabled={busy || !canMutate || selected.size === 0}
              onClick={onAdd}
              sx={accessAnalyzerButtonSx}
            >
              {selected.size === 0 ? 'Add to Access Analyzer' : `Add ${selected.size} to Access Analyzer`}
            </Button>
          )}
        />
        {/* Suppressed when setup failed - the terminal banner above already explains
            it, and this copy would contradict it. What is left is in_progress,
            which the wording is then accurate for. */}
        {!canMutate && !setupFailed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enrollment changes are paused while the current operation finishes.
          </Alert>
        )}
        {config.unusedAccessEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unused-access analysis is on, so each account added here also gets a paid
            unused-access analyzer in {config.homeRegion} - $0.20 per IAM role or user
            per month, per account.
          </Alert>
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                    checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                    disabled={notEnrolled.length === 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>Account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notEnrolled.map((a, index) => (
                <TableRow key={a.accountId} hover sx={rowSx(index)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(a.accountId)}
                      onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(a.accountId);
                        else n.delete(a.accountId);
                        setSelected(n);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {acctLabel(a.accountId)}
                    {removedIds.has(a.accountId) && (
                      <Chip
                        label="previously removed"
                        size="small"
                        sx={{ ml: 1, ...statusChipSx('neutral') }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {notEnrolled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
                      title="Every linked account is enrolled"
                      description="Nothing left to add - new accounts appear here once they join the organization."
                      color={ACCESS_ANALYZER_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Remove account from Access Analyzer</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
            Remove <b>{confirmRemove}</b>? Prism deletes the analyzers it created in
            that account - in every analyzed region, plus its unused-access analyzer
            if that family is on - removes its cached findings here, and detaches the
            IAM policy it added to the account&apos;s access role. Analyzers Prism
            adopted are left running, along with their findings. Re-adding the account
            later re-grants it and starts fresh analysis.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmRemove(null)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => onRemove(confirmRemove)}
            disabled={busy}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
